import {
  fetchApi,
  getNetworkInfo,
  getTransifexLocale,
  trackError,
} from '@vend/utilities'
import { tx as heliosTxInstance } from '@lightspeed/design-system-react'
import { REACT_UTILITIES_TRANSIFEX_TAG } from '@vend/react-utilities'
import { BUSINESS_COMPONENTS_TRANSLATION_TAG } from '@vend/business-components'
import { createNativeInstance, tx } from '@transifex/native'
import { ApiReceiptTemplateType } from '@vend/types'
import { receiptSettingsStore } from '../factories/database/receipt-settings-store'
import { receiptTemplateTypesStore } from '../factories/database/receipt-template-types-store'

const txManagerModulePromise = window.System.import('@vend-spa/tx-manager')
let fetchTranslationsPromise: Promise<any>

const TRANSIFEX_LOADING_TIMEOUT = 10000
const WEB_REGISTER_TX_TAG = 'webregister'
const TRANSIFEX_APP_TOKEN = '1/af8dd5b9fec79f9ea0fb7f7dab4842261ff5d5ec'

let defaultTXInstance: typeof tx

const localeTXInstances = new Map<string, typeof tx>()
const prefixedTemplateLocales = new Set<string>()

// Used by wr-printer.ts to build the receiptTemplateTypes lookup key.
// e.g. getReceiptTemplateSuffix('fr-FR') returns '__fr-FR', so the printer
// looks up "thermal__fr-FR" instead of "thermal" in IndexedDB.
export function getReceiptTemplateSuffix(locale: string): string {
  return `__${locale}`
}

// Used by wr-printer.ts to check if a suffixed HTML template exists in IndexedDB
// for this locale. If true, the printer appends the suffix to the template name.
// If false, the printer uses the base template name (e.g. "thermal").
export function hasReceiptTemplateTypesForLocale(locale: string): boolean {
  return prefixedTemplateLocales.has(locale)
}

txManagerModulePromise.then(({ getTxInstance }: any) => {
  defaultTXInstance = getTxInstance(WEB_REGISTER_TX_TAG)
})

/**
 * Fetches translations for React based components.
 *
 * @param disableTranslations - Whether to disable translations
 * @returns {Promise<void>}
 */
export async function fetchTranslations(disableTranslations: boolean) {
  if (fetchTranslationsPromise) {
    return fetchTranslationsPromise
  }

  if (disableTranslations) {
    return Promise.resolve()
  }

  fetchTranslationsPromise = txManagerModulePromise
    .then(({ setCurrentLocale, getTxInstance, addTxInstance }: any) => {
      const tx = getTxInstance(WEB_REGISTER_TX_TAG)

      const reactUtilitiesTxInstance = getTxInstance(
        REACT_UTILITIES_TRANSIFEX_TAG
      )
      const businessComponentsTxInstance = getTxInstance(
        BUSINESS_COMPONENTS_TRANSLATION_TAG
      )

      // Adds Helios instance to the tx instance cache, ensuring common config is applied (inc. custom CDS)
      addTxInstance(
        heliosTxInstance,
        'helios-react',
        heliosTxInstance.token,
        `helios-react-${WEB_REGISTER_TX_TAG}`
      )

      tx.controllerOf(reactUtilitiesTxInstance)
      tx.controllerOf(heliosTxInstance)
      tx.controllerOf(businessComponentsTxInstance)

      const locale = getTransifexLocale()

      const timeoutPromise = new Promise((resolve, reject) => {
        setTimeout(() => {
          reject(
            new Error(`Timed out attempting to load translations for ${locale}`)
          )
        }, TRANSIFEX_LOADING_TIMEOUT)
      })

      const txCurrentLocalePromise = setCurrentLocale(locale).catch(() => {
        throw new Error(`Can't fetch translations for ${locale}`)
      })

      return Promise.race([txCurrentLocalePromise, timeoutPromise])
    })
    .catch((e: Error) => {
      trackError(e.message, { networkInfo: getNetworkInfo() })
    })

  return fetchTranslationsPromise
}

/**
 * Determines which receipt locales need their own translation instances and template types.
 * Replaces the per-locale functions with a single generic flow.
 */
export const determineReceiptLocaleSettings = async () => {
  try {
    // Read all receiptSettings (labels, locale, display flags) from IndexedDB to find
    // which locales are configured. Each entry has a locale property (e.g. "fr-FR", "nl-NL").
    const receipts = await receiptSettingsStore.all()
    // Gets the browser language. Translations for this language are already loaded at startup
    // by the default Transifex instance
    const appLocale = getTransifexLocale()

    // From receiptSettings, collect locales that differ from the browser language.
    // e.g. if browser is nl-NL and receipts have fr-FR and nl-NL, only fr-FR is collected.
    // We need other locales to have different Transifex instances.
    const receiptLocales = new Set(
      receipts
        .map((r: any) => r.locale as string)
        .filter((locale: string) => locale && locale !== appLocale)
    )
    // All receipts match the browser language — the default instance covers them, nothing to do.
    if (receiptLocales.size === 0) {
      return
    }

    // Read all receiptTemplateTypes (HTML/CSS templates like "thermal", "a4") from IndexedDB.
    // We check this separately from receiptSettings to see if locale-specific HTML templates
    // have already been fetched and stored from a previous load.
    const templates = await receiptTemplateTypesStore.all()

    for (const locale of receiptLocales) {
      // Create a dedicated Transifex translation instance for this locale
      retrieveTranslationsForLocale(locale)

      const suffix = getReceiptTemplateSuffix(locale)

      // Check if locale-suffixed HTML templates exist in receiptTemplateTypes IndexedDB
      // (e.g. "thermal__fr-CA") — these are copies of the base HTML template fetched
      // from /api/2.0/receipt_template_types with Accept-Language set to this locale.
      const hasSuffixedTemplates = templates.some((t: any) =>
        t.name.includes(suffix)
      )
      // Check if HTML templates in receiptTemplateTypes have a matching locale property
      // but without a name suffix (e.g. name: "thermal", locale: "nl-NL")
      const hasLocaleTemplates = templates.some((t: any) => t.locale === locale)
      if (hasSuffixedTemplates) {
        // 2nd+ load: suffixed HTML templates (e.g. "thermal__fr-FR") were stored in
        // receiptTemplateTypes IndexedDB on a previous load. Register this locale so
        // the printer looks up the suffixed template instead of "thermal".
        prefixedTemplateLocales.add(locale)
      } else if (!hasLocaleTemplates) {
        // 1st load: no HTML templates for this locale exist in receiptTemplateTypes —
        // fetch them from /api/2.0/receipt_template_types with Accept-Language header,
        // then store in IndexedDB with a suffixed name (e.g. "thermal__fr-FR").
        // On subsequent loads, the hasSuffixedTemplates branch above will find them.
        const templateTypes = await retrieveTemplateTypesForLocale(locale)
        if (templateTypes) {
          await addReceiptTemplateTypesToStore(templateTypes, locale)
        }
      }
    }
  } catch (error: any) {
    trackError(error?.message)
  }
}

/**
 * Creates a Transifex translation instance for the given locale if one doesn't exist yet.
 */
const retrieveTranslationsForLocale = (locale: string) => {
  if (localeTXInstances.has(locale)) {
    return
  }

  const instance = createNativeInstance({
    token: TRANSIFEX_APP_TOKEN,
    filterTags: WEB_REGISTER_TX_TAG,
  })

  // Transifex uses underscores (e.g. fr_CA, nl) not hyphens
  const txLocale = locale.replace('-', '_')
  instance.setCurrentLocale(txLocale)
  localeTXInstances.set(locale, instance)
}

/**
 * Retrieves receipt template types from the API for the given locale.
 */
const retrieveTemplateTypesForLocale = async (locale: string) => {
  try {
    const response = await fetchApi('/api/2.0/receipt_template_types', {
      method: 'GET',
      headers: {
        'Accept-Language': locale,
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to retrieve template types for ${locale}`)
    }

    const { data } = await response.json()
    return data
  } catch (error: any) {
    trackError(error?.message)
  }
}

/**
 * Adds the provided receipt template types to the ReceiptTemplateTypes store,
 * prefixing the name with the locale suffix.
 */
const addReceiptTemplateTypesToStore = async (
  templateTypes: ApiReceiptTemplateType[],
  locale: string
) => {
  try {
    const suffix = getReceiptTemplateSuffix(locale)
    for (const template of templateTypes) {
      await receiptTemplateTypesStore.put({
        ...template,
        name: `${template.name}${suffix}`,
      })
    }

    prefixedTemplateLocales.add(locale)
  } catch (error: any) {
    trackError(error?.message)
  }
}

/**
 * Returns the appropriate translate function for the provided receipt locale.
 */
export const getTForReceiptLocale = (receiptSettingLocale?: string) => {
  if (receiptSettingLocale) {
    const instance = localeTXInstances.get(receiptSettingLocale)
    if (instance) {
      return instance.t
    }
  }

  return defaultTXInstance.t
}
